// Helpers purs pour les rappels d'échéance des paiements en plusieurs

import { PaymentPlan, PaymentPlanStatus } from '../types/paymentPlans';
import { NotificationType } from '../types/notifications';
import i18n from '../i18n';

import { formatCurrency } from './numberFormat';

export type PaymentPlanReminderKind = '2d' | '1d' | 'due';

export const PAYMENT_PLAN_REMINDER_TYPES: Record<
  PaymentPlanReminderKind,
  NotificationType
> = {
  '2d': 'payment_plan_reminder_2d',
  '1d': 'payment_plan_reminder_1d',
  due: 'payment_plan_reminder_due',
};

const ACTIVE_STATUSES: PaymentPlanStatus[] = ['pending', 'in_progress'];

export const startOfLocalDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const diffLocalCalendarDays = (from: Date, to: Date): number => {
  const fromDay = startOfLocalDay(from).getTime();
  const toDay = startOfLocalDay(to).getTime();
  return Math.round((toDay - fromDay) / (24 * 60 * 60 * 1000));
};

export const isPaymentPlanEligibleForReminder = (plan: Pick<
  PaymentPlan,
  'status' | 'amountRemaining' | 'targetDate'
>): boolean => {
  if (!plan.targetDate) return false;
  if (!ACTIVE_STATUSES.includes(plan.status)) return false;
  return (plan.amountRemaining ?? 0) > 0;
};

export const getPaymentPlanReminderKind = (
  targetDate: Date,
  referenceDate: Date = new Date()
): PaymentPlanReminderKind | null => {
  const daysUntilDue = diffLocalCalendarDays(referenceDate, targetDate);
  if (daysUntilDue === 2) return '2d';
  if (daysUntilDue === 1) return '1d';
  if (daysUntilDue === 0) return 'due';
  return null;
};

export const getPaymentPlanReminderType = (
  targetDate: Date,
  referenceDate: Date = new Date()
): NotificationType | null => {
  const kind = getPaymentPlanReminderKind(targetDate, referenceDate);
  return kind ? PAYMENT_PLAN_REMINDER_TYPES[kind] : null;
};

export const formatPaymentPlanReminderAmount = (amount: number): string =>
  formatCurrency(Math.round(amount), 0);

export const buildPaymentPlanReminderMessage = (input: {
  kind: PaymentPlanReminderKind;
  clientName: string;
  amountRemaining: number;
  saleReceiptNumber?: string;
  targetDate: Date;
}): string => {
  const amountLabel = formatPaymentPlanReminderAmount(input.amountRemaining);
  const receiptSuffix = input.saleReceiptNumber ? ` (${input.saleReceiptNumber})` : '';
  const dateLabel = input.targetDate.toLocaleDateString('fr-FR');

  if (input.kind === '2d') {
    return `📅 Échéance dans 2 jours : ${input.clientName} — reste ${amountLabel}${receiptSuffix}`;
  }
  if (input.kind === '1d') {
    return `⏰ Échéance demain : ${input.clientName} — reste ${amountLabel}${receiptSuffix}`;
  }
  return `🚨 Échéance aujourd'hui (${dateLabel}) : ${input.clientName} — reste ${amountLabel}${receiptSuffix}`;
};

export const getPaymentPlanReminderSeverity = (
  kind: PaymentPlanReminderKind
): 'info' | 'warning' | 'critical' => {
  if (kind === 'due') return 'critical';
  if (kind === '1d') return 'warning';
  return 'info';
};

export const PAYMENT_PLAN_REMINDER_TYPE_LABELS: Partial<Record<NotificationType, string>> = {
  get payment_plan_reminder_2d() {
    return String(i18n.t('typeLabel.payment_plan_reminder_2d', { ns: 'notifications' }));
  },
  get payment_plan_reminder_1d() {
    return String(i18n.t('typeLabel.payment_plan_reminder_1d', { ns: 'notifications' }));
  },
  get payment_plan_reminder_due() {
    return String(i18n.t('typeLabel.payment_plan_reminder_due', { ns: 'notifications' }));
  },
};
